/* Wishlist hearts. ONE client for every heart on the storefront:
 *   Cycle 5  the PDP heart
 *   Cycle 6  a heart on each product card (collection grids, /search, homepage)
 *   Cycle 7  a heart on each search-overlay result row
 *
 * Cycle 7 changed how hearts are bound, because overlay results do not exist
 * at load time -- they are built by assets/svz-search-overlay.js while the
 * customer types. Two consequences:
 *
 *   1. Clicks are handled by ONE delegated listener on document, so a heart
 *      that appears later is live the moment it is inserted. Per-heart
 *      addEventListener could never have covered overlay rows, and adding a
 *      second client for them was explicitly not an option.
 *
 *   2. The session read is lazy and memoised. It fires the first time any
 *      heart actually needs its saved state, and exactly once per page --
 *      not once per heart, and not at all on a page whose only hearts are
 *      overlay rows the customer never opens.
 *
 * Overlay code calls window.SvzWishlist.refresh(container) after rendering,
 * which paints the saved state onto the new hearts. That is the only coupling
 * between the two files.
 *
 * Payload attributes are namespaced data-wish-* on purpose. A bare data-price
 * on a heart would be captured by pdp-breakdown.js / pdp-variant-sync.js,
 * which both resolve the PDP's live price element with
 * querySelector('[data-price]') -- the heart sits above .price-lg, so the
 * formula price would land in the button. Caught in Cycle 6 verification.
 *
 * UNITS: data-wish-price is always PAISE (Shopify's integer product.price),
 * because assets/svz-account.js renders the Wishlist row as inr(w.price/100).
 * Liquid hearts pass product.price straight through; the overlay converts,
 * since predictive search reports price_min in rupees ("25540.00").
 *
 * Actions are unchanged and still the two proven against the live backend --
 * wishlist_add / wishlist_remove, the same pair assets/svz-account.js uses
 * for the account Wishlist tab. No new endpoint, no third action name.
 */
(function () {
  "use strict";

  var cfg = window.__SVZ_WISHLIST_CONFIG__ || {};
  var SEL = "[data-wishlist-heart]";

  function hearts(root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(SEL));
  }

  function idOf(btn) {
    return String(btn.getAttribute("data-wish-id") || "");
  }

  function token() {
    try {
      return (window.SvzAuth && window.SvzAuth.getToken && window.SvzAuth.getToken()) || "";
    } catch (e) {
      return "";
    }
  }

  function loggedIn() {
    return !!(token() && window.SvzAuth && window.SvzAuth.api);
  }

  var saved = null;        // { productId: true } once known
  var savedPromise = null; // memoised session read

  function loadSaved() {
    if (savedPromise) return savedPromise;
    savedPromise = window.SvzAuth.api(cfg, { action: "session", token: token() })
      .then(function (session) {
        saved = {};
        ((session && session.wishlist) || []).forEach(function (w) {
          if (w && w.product_id != null) saved[String(w.product_id)] = true;
        });
        return saved;
      })
      .catch(function () {
        /* Never blocks the page: on failure the hearts simply stay outlined. */
        saved = saved || {};
        return saved;
      });
    return savedPromise;
  }

  function paint(btn, active) {
    btn.classList.toggle("is-active", active);
    btn.setAttribute("aria-pressed", active ? "true" : "false");
  }

  /* Painted onto every heart for the same product, not just the one clicked --
   * a page can show one product twice (a card, plus an overlay result for it),
   * and a half-filled pair reads as a bug. The cache is updated too, so a
   * later refresh() does not repaint the stale state. */
  function paintAll(productId, active) {
    var key = String(productId);
    if (saved) saved[key] = !!active;
    hearts().forEach(function (btn) {
      if (idOf(btn) === key) paint(btn, active);
    });
  }

  /* Public: paint saved state onto hearts inside `root` (default: document).
   * Safe to call repeatedly and before login -- it no-ops when there is
   * nothing to paint or nobody is signed in. */
  function refresh(root) {
    var list = hearts(root);
    if (!list.length || !loggedIn()) return;
    loadSaved().then(function (map) {
      list.forEach(function (btn) {
        if (map[idOf(btn)]) paint(btn, true);
      });
    });
  }

  document.addEventListener("click", function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var btn = t.closest(SEL);
    if (!btn) return;

    ev.preventDefault();

    /* Logged out: every heart is just a login door, same pattern as every
     * other account-gated control on the storefront -- no add/remove call is
     * ever attempted without a token, and `next` returns the customer to the
     * exact page (grid, PDP, search results or the page they opened the
     * overlay from) they clicked on. */
    if (!loggedIn()) {
      var next = window.location.pathname + window.location.search;
      window.location.href = (cfg.loginUrl || "/pages/login") + "?next=" + encodeURIComponent(next);
      return;
    }

    if (btn.disabled) return;

    var productId = idOf(btn);
    var isActive = btn.classList.contains("is-active");
    var payload = {
      action: isActive ? "wishlist_remove" : "wishlist_add",
      token: token(),
      product_id: productId
    };

    if (!isActive) {
      /* price stays a Number in paise -- see the UNITS note above. */
      var price = Number(btn.getAttribute("data-wish-price"));
      payload.title = btn.getAttribute("data-wish-title") || "";
      payload.image_url = btn.getAttribute("data-wish-image") || "";
      payload.handle = btn.getAttribute("data-wish-handle") || "";
      if (!isNaN(price)) payload.price = price;
    }

    btn.disabled = true;
    paintAll(productId, !isActive); // optimistic

    window.SvzAuth.api(cfg, payload)
      .then(function (j) {
        if (!j || j.ok !== true) paintAll(productId, isActive); // revert on failure
      })
      .catch(function () {
        paintAll(productId, isActive); // revert on network failure
      })
      .then(function () {
        btn.disabled = false;
      });
  });

  refresh();

  window.SvzWishlist = { refresh: refresh };
})();
